/**
 * Module-level service that registers state contributors, saves a snapshot before
 * an OAuth redirect, and restores it on the next cold boot.
 */

import { appSessionRestoreSlice, ephemeralStore } from "../../store/ephemeral";
import {
    APP_SESSION_MAX_SIZE_BYTES,
    APP_SESSION_RESTORE_TTL_MS,
    APP_SESSION_STORAGE_BACKEND,
    APP_SESSION_STORAGE_KEY,
} from "./sessionRestore.config";
import {
    APP_SESSION_SNAPSHOT_VERSION,
    type AppSessionSnapshot,
    type AppSessionSnapshotKey,
} from "./sessionRestore.types";

/**
 * Context passed to a contributor's `restore` callback.
 */
export interface RestoreContext {

    /** Full snapshot being restored. */
    "snapshot": AppSessionSnapshot;

    /** Whether the restore session is still active. */
    "isRestoring": boolean;
}

/**
 * A contributor can capture a portion of the app state and restore it later.
 * @typeParam TValue Type of the captured value for this contributor.
 */
export interface SessionStateContributor<TValue> {

    /** Unique key used in the snapshot and restore tracking. */
    "key": AppSessionSnapshotKey;

    /** Restoration order: lower numbers are restored first. */
    "priority"?: number;

    /** Returns the value to save, or `null` when nothing should be saved. */
    "capture": () => TValue | null;

    /** Applies the saved value back to the UI. */
    "restore": ( value: TValue, context: RestoreContext ) => void | Promise<void>;
}

/**
 * Public surface of the session restore service.
 */
export interface SessionRestoreService {

    /** Registers a contributor and returns an unregistration function. */
    "register": <TValue>( contributor: SessionStateContributor<TValue> ) => () => void;

    /** Captures and writes the snapshot to storage. */
    "saveAppState": () => boolean;

    /** Reads and validates the snapshot from storage. */
    "tryRestoreOnBoot": () => AppSessionSnapshot | null;

    /** Marks a contributor key as restored. */
    "markRestored": ( key: string ) => void;

    /** Removes the stored snapshot. */
    "clearSnapshot": () => void;

    /** Returns the currently registered contributor keys. */
    "getRegisteredKeys": () => string[];

    /** Resets the service for unit tests. */
    "resetForTests": () => void;
}

/**
 * Internal contributor record with erased generics.
 */
interface RegisteredContributor {
    "key":      string;
    "priority": number;
    "capture":  () => unknown;
    "restore":  ( value: unknown, context: RestoreContext ) => void | Promise<void>;
}

/** Active contributor registry. */
const contributors: RegisteredContributor[] = [];

/**
 * Returns the storage object chosen for the current platform.
 * @returns `localStorage` on native, otherwise `sessionStorage`.
 */
function getStorage(): Storage {
    return APP_SESSION_STORAGE_BACKEND === "localStorage" ? localStorage : sessionStorage;
}

/**
 * Determines whether a snapshot has enough content to be saved or restored.
 * @param snapshot - Candidate snapshot.
 * @returns `true` when the snapshot contains a planner session.
 */
function hasSnapshotContent( snapshot: AppSessionSnapshot ): boolean {
    return snapshot.plannerSession !== undefined;
}

/**
 * Singleton service that coordinates session save/restore contributors.
 */
export const sessionRestoreService: SessionRestoreService = {
    "register": <TValue>( contributor: SessionStateContributor<TValue> ): () => void => {
        const entry: RegisteredContributor = {
            "key":      contributor.key,
            "priority": contributor.priority ?? 0,
            "capture":  contributor.capture as () => unknown,
            "restore":  contributor.restore as ( value: unknown, context: RestoreContext ) => void | Promise<void>,
        };

        contributors.push( entry );
        contributors.sort( ( left, right ) => left.priority - right.priority );

        return () => {
            const index = contributors.indexOf( entry );
            if ( index !== -1 ) {
                contributors.splice( index, 1 );
            }
        };
    },

    "saveAppState": (): boolean => {
        if ( typeof window === "undefined" ) {
            return false;
        }

        const snapshot: AppSessionSnapshot = {
            "version": APP_SESSION_SNAPSHOT_VERSION,
            "savedAt": Date.now(),
        };

        for ( const contributor of contributors ) {
            const value = contributor.capture();
            if ( value !== null ) {
                ( snapshot as unknown as Record<string, unknown> )[contributor.key] = value;
            }
        }

        if ( !hasSnapshotContent( snapshot ) ) {
            return false;
        }

        const json = JSON.stringify( snapshot );

        if ( json.length > APP_SESSION_MAX_SIZE_BYTES ) {
            console.warn( `[SessionRestoreService] Snapshot too large (${ json.length } bytes), skipping save.` );

            return false;
        }

        try {
            getStorage().setItem( APP_SESSION_STORAGE_KEY, json );
        } catch( error ) {
            console.warn( "[SessionRestoreService] Failed to write snapshot", error );

            return false;
        }

        return true;
    },

    "tryRestoreOnBoot": (): AppSessionSnapshot | null => {
        if ( typeof window === "undefined" ) {
            return null;
        }

        const raw = getStorage().getItem( APP_SESSION_STORAGE_KEY );
        if ( raw === null ) {
            return null;
        }

        let snapshot: AppSessionSnapshot;
        try {
            snapshot = JSON.parse( raw ) as AppSessionSnapshot;
        } catch {
            sessionRestoreService.clearSnapshot();

            return null;
        }

        if ( snapshot.version !== APP_SESSION_SNAPSHOT_VERSION ) {
            sessionRestoreService.clearSnapshot();

            return null;
        }

        if ( typeof snapshot.savedAt !== "number" || Number.isNaN( snapshot.savedAt ) ) {
            sessionRestoreService.clearSnapshot();

            return null;
        }

        if ( Date.now() - snapshot.savedAt > APP_SESSION_RESTORE_TTL_MS ) {
            sessionRestoreService.clearSnapshot();

            return null;
        }

        if ( !hasSnapshotContent( snapshot ) ) {
            sessionRestoreService.clearSnapshot();

            return null;
        }

        return snapshot;
    },

    "markRestored": ( key: string ): void => {
        ephemeralStore.setValue( appSessionRestoreSlice, ( previous ) => {
            if ( previous.restoredKeys.includes( key ) ) {
                return previous;
            }

            return {
                ...previous,
                "restoredKeys": [ ...previous.restoredKeys, key ],
            };
        } );
    },

    "clearSnapshot": (): void => {
        if ( typeof window === "undefined" ) {
            return;
        }

        getStorage().removeItem( APP_SESSION_STORAGE_KEY );
    },

    "getRegisteredKeys": (): string[] => contributors.map( ( contributor ) => contributor.key ),

    "resetForTests": (): void => {
        contributors.length = 0;
        sessionRestoreService.clearSnapshot();
    },
};
